import { apartmentIcon, interviewerIcon, logoIcon } from 'app/components/Icons/icons';
import React, { useEffect, useMemo, useState } from 'react';
import IconCommonStyle from 'app/components/Icons/IconCommonStyle';
import CommonDropdown from '../../common/CommonDropdown';
import { MenuProps, Switch, Tooltip } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../types';
import {
  clearAllData,
  convertDate,
  convertTime,
  convertValueToString,
  replacePathParams,
} from '../../../helpers/funcs';
import CommonSelect from '../../common/CommonSelect';
import PlaceIcon from '../../../assets/icon/place.svg';
import DocumentIcon from '../../../assets/icon/document.svg';
import UserIcon from '../../../assets/icon/user.svg';
import { setFacilityName, setIsScreenShareMode } from '../../../store/implements/implementsSlice';
import CommonModal from '../../common/modal/CommonModal';
import { AccountSettingModal } from './AccountSettingModal';
import { getInfoCom } from '../../../store/com/comSlice';
import { getInfoManager } from '../../../store/manager/managerSlice';
import { getListOmoteura } from '../../../store/omoteura/omoteuraSlice';
import { useServices } from '../OmoteraDiagnosisResultPage/service';
import { expires } from '../../../helpers/cookies';
import { getUserFromLocalStorage, saveUseToLocalStorage } from '../../../helpers/localStorage';
import { axiosClient } from '../../../api/axiosClient';
import { implementApi } from '../../../api/implementApi';
import { TImplement } from '../../../types/clientManagement/clientManagement';
import { EPath } from '../../routes/routesConfig';
import logo from '../../assets/images/logo.png';
import { getInfoEmployee } from '../../../store/employee/employeeSlice';

export default function MyHeader() {
  const dispatch = useDispatch();
  const { setEventSelectedConsultant, eventSelectedConsultant } = useServices();
  const navigate = useNavigate();
  const location = useLocation();

  const { interviewDetail } = useSelector((state: RootState) => state.interviewer);
  const { isShowListEvent, facilityNameSelected, employeeNameSelected, isScreenShareMode } =
    useSelector((state: RootState) => state.implement);
  const [currentUser, setCurrentUser] = useState(getUserFromLocalStorage());
  const [isOpenModalAccountSetting, setIsOpenModalAccountSetting] = useState<boolean>(false);
  // getContentHeader() only renders for 面談員, so its side effects must stay 面談員-only too:
  // com/client_manager/employee drive the same routes from their own in-page bar, and a
  // second navigate() from here fights with theirs.
  const isConsultant = !!currentUser.consultant;
  const isConsultantResultPage =
    location.pathname.includes('/additional-interview/omoteura-diagnosis-result') ||
    location.pathname.includes('interviews/omoteura-diagnosis-result');
  // /:employeeId/:eventId for both 実施回 and 追加面談 result routes.
  const urlEmployeeId = convertValueToString(location.pathname.split('/')[3]);
  const urlEventId = convertValueToString(location.pathname.split('/')[4]);
  const urlEmployeeProgressId = useMemo(
    () => new URLSearchParams(location.search).get('employeeProgressId') ?? '',
    [location.search],
  );
  // Same list the com in-page bar builds its select from: 実施回 + 追加面談 together, one row per
  // employee_progress, already labelled 【通常】/【追加】 by the API. Fetched here rather than read
  // off useServices() because MyLayout sits on the parent '*' route — the :employeeId params
  // belong to a nested <Routes>, so useParams() is empty in this component.
  const [listEventByEmployeeAll, setListEventByEmployeeAll] = useState<TImplement[]>([]);
  const { infoCom } = useSelector((state: RootState) => state.com);
  const { infoManager } = useSelector((state: RootState) => state.manager);
  const { infoEmployee } = useSelector((state: RootState) => state.employee);
  const { currentUserChat: currentUserChatRedux } = useSelector((state: RootState) => state.auth);

  const handleClose = () => setIsOpenModalAccountSetting(false);

  useEffect(() => {
    if (!isConsultant || !isConsultantResultPage || !urlEmployeeId) {
      setListEventByEmployeeAll([]);
      return;
    }
    let cancelled = false;
    implementApi
      .getListEventByEmployeeAll(urlEmployeeId)
      .then((response) => {
        if (cancelled) {
          return;
        }
        const data = response?.data?.data;
        setListEventByEmployeeAll(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) {
          setListEventByEmployeeAll([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isConsultant, isConsultantResultPage, urlEmployeeId]);

  // The API returns one row per employee_progress, so an event can appear more than once;
  // match the progress from the URL first and fall back to the event's first row.
  const combineEventValue = (eventValue: string, progressValue: string) =>
    `${eventValue}::${progressValue}`;
  const selectedEventOptionValue = useMemo(() => {
    const rowsOfEvent = listEventByEmployeeAll.filter(
      (item) => convertValueToString(item?._id ?? (item as any)?.eventId) === urlEventId,
    );
    if (rowsOfEvent.length === 0) {
      return combineEventValue(urlEventId, urlEmployeeProgressId);
    }
    const matchedRow =
      rowsOfEvent.find(
        (item) => convertValueToString((item as any)?.employeeProgressId) === urlEmployeeProgressId,
      ) ?? rowsOfEvent[0];
    return combineEventValue(
      convertValueToString(matchedRow?._id ?? (matchedRow as any)?.eventId),
      convertValueToString((matchedRow as any)?.employeeProgressId),
    );
  }, [listEventByEmployeeAll, urlEventId, urlEmployeeProgressId]);

  const handleSetEventSelected = (combinedValue: string) => {
    const [selectedEventId = '', selectedProgressId = ''] =
      convertValueToString(combinedValue).split('::');
    if (!selectedEventId || !urlEmployeeId) {
      return;
    }
    const selectedEvent = listEventByEmployeeAll.find(
      (item) =>
        convertValueToString(item?._id ?? (item as any)?.eventId) === selectedEventId &&
        (!selectedProgressId ||
          convertValueToString((item as any)?.employeeProgressId) === selectedProgressId),
    );
    const isAdditionalEvent =
      convertValueToString((selectedEvent as any)?.eventType).toLowerCase() === 'additional';
    // 面談員 stays on the consultant route for 実施回 — /implements/... is the com-side page and
    // renders neither this bar nor the in-page one for a consultant.
    const destinationPath = isAdditionalEvent
      ? EPath.additionalOmoteuraDiagnosi
      : EPath.omoteuraDiagnosiConsultant;
    const destinationUrl = replacePathParams(destinationPath, {
      employeeId: urlEmployeeId,
      eventId: selectedEventId,
    });
    const employeeProgressId =
      selectedProgressId || convertValueToString((selectedEvent as any)?.employeeProgressId);
    // Only 追加面談 reads employeeProgressId off the query string; keep the 実施回 URL as it is.
    navigate(
      isAdditionalEvent && employeeProgressId
        ? `${destinationUrl}?employeeProgressId=${encodeURIComponent(employeeProgressId)}`
        : destinationUrl,
    );
  };

  useEffect(() => {
    setEventSelectedConsultant(urlEventId);
    // Only the 診断結果 pages own this value; elsewhere it is set from the interview detail
    // (interviewerSlice) or from the row the 面談員 clicked (Consultant/MyPage), so overwriting
    // outside those pages would clobber a better value.
    if (!isConsultant || !isConsultantResultPage) {
      return;
    }
    const facilityNameOfEvent =
      listEventByEmployeeAll.find((item) => convertValueToString(item?._id) === urlEventId)
        ?.facilityName ?? listEventByEmployeeAll[0]?.facilityName;
    facilityNameOfEvent && dispatch(setFacilityName(facilityNameOfEvent));
  }, [listEventByEmployeeAll, location.pathname, isConsultantResultPage, isConsultant, urlEventId]);

  useEffect(() => {
    // The 実施回 consultant page does not fetch omoteura itself — this is the only place that
    // does. 追加面談 has its own fetch (it forwards employeeProgressId), so it is left alone.
    if (
      isConsultant &&
      location.pathname.includes('interviews/omoteura-diagnosis-result') &&
      eventSelectedConsultant
    ) {
      dispatch(
        getListOmoteura({
          employeeId: urlEmployeeId,
          eventId: convertValueToString(eventSelectedConsultant),
        }),
      );
    }
  }, [eventSelectedConsultant, urlEmployeeId, isConsultant]);

  const items: MenuProps['items'] = [
    {
      label: (
        <div
          className="flex gap-2 p-2 items-center h-10"
          onClick={() => {
            if (currentUser.consultant) {
              navigate('/account-setting');
            } else {
              setIsOpenModalAccountSetting(true);
            }
          }}
        >
          <span className="text-base font-normal leading-[24px] tracking-wide">アカウント設定</span>
        </div>
      ),
      key: '0',
    },
    {
      label: (
        <div className="flex gap-2 p-2 items-center h-10" onClick={clearAllData}>
          <span className="text-base font-normal leading-[24px] tracking-wide">ログアウト</span>
        </div>
      ),
      key: '1',
    },
  ];

  useEffect(() => {
    setCurrentUser(getUserFromLocalStorage());
  }, [JSON.stringify(getUserFromLocalStorage())]);

  const getNameUser = useMemo(() => {
    if (Object.keys(currentUser).length > 0) {
      return (
        currentUser?.com?.name ??
        currentUser?.employee?.name ??
        currentUser?.consultant?.name ??
        currentUser?.manager?.name
      );
    }
    return '';
  }, [JSON.stringify(getUserFromLocalStorage()), currentUser]);

  const getInfo = async () => {
    axiosClient.get(`/consultant/${getUserFromLocalStorage()?.consultant?._id}`).then((e) => {
      const newUser = getUserFromLocalStorage();
      const modifyUser = {
        ...newUser,
        name: e.data.data.name,
      };
      setCurrentUser(modifyUser);
      saveUseToLocalStorage(modifyUser);
    });
  };

  useEffect(() => {
    const user = getUserFromLocalStorage();
    if (user.role === 'consultant') {
      getInfo();
    }
  }, [JSON.stringify(getUserFromLocalStorage())]);

  useEffect(() => {
    if (currentUser.com) {
      dispatch(getInfoCom(currentUser.com._id));
    } else if (currentUser.manager) {
      dispatch(getInfoManager(currentUser.manager._id));
    } else if (currentUser.employee) {
      dispatch(getInfoEmployee(currentUser.employee._id));
    }
  }, []);

  const getContentHeader = (pathName: string) => {
    switch (true) {
      case pathName.includes('/interviews/omoteura') ||
        pathName.includes('/additional-interview/omoteura-diagnosis-result'):
        return (
          <div className="flex items-center min-w-[1000px] gap-6 py-2 px-6 bg-gainsboro shadow-[0px_4px_4px_0px_#00000040]">
            <div className="text-lg w-52 shrink-0 text-jet-black whitespace-nowrap">
              現在のコンディション
            </div>
            {isShowListEvent && (
              // Wraps onto a second row instead of squeezing a single child: a long
              // eventName used to collapse the note below into a one-character column,
              // which grew the bar tall enough to fill a 1280px-wide viewport.
              <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-1 flex-1 min-w-0">
                <div className="flex items-center justify-start min-w-[200px] w-80 shrink-0">
                  {listEventByEmployeeAll.length > 0 && (
                    <CommonSelect
                      options={listEventByEmployeeAll.map((implement: any) => {
                        return {
                          value: combineEventValue(
                            convertValueToString(implement?._id ?? implement?.eventId),
                            convertValueToString(implement?.employeeProgressId),
                          ),
                          label: convertValueToString(implement?.eventName),
                        };
                      })}
                      value={selectedEventOptionValue}
                      onChangeValue={handleSetEventSelected}
                    />
                  )}
                </div>
                <div className="shrink-0 whitespace-nowrap">
                  あくまでも、受信時点のコンディションを示したものです。
                  <br />
                  コンディションは、時と共に変化することを前提にご利用ください。
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <div className="flex items-center gap-2 mr-2">
                    <span className="text-base whitespace-nowrap">画面共有モード</span>
                    <Switch
                      checked={isScreenShareMode}
                      onChange={(checked) => dispatch(setIsScreenShareMode(checked))}
                      className={isScreenShareMode ? '!bg-light-orange' : '!bg-medium-gray'}
                    />
                  </div>
                  <Tooltip title={facilityNameSelected}>
                    <div className="flex gap-2 h-[23px]">
                      <img src={PlaceIcon} alt="" />
                      <span className="max-w-[200px] text-base whitespace-nowrap text-ellipsis overflow-hidden">
                        {facilityNameSelected}
                      </span>
                    </div>
                  </Tooltip>
                  <Tooltip title={employeeNameSelected}>
                    <div className="flex gap-2 h-[23px]">
                      <img src={UserIcon} alt="" />
                      <span className="max-w-[200px] text-base whitespace-nowrap text-ellipsis overflow-hidden">
                        {employeeNameSelected}
                      </span>
                      <img src={DocumentIcon} alt="" />
                    </div>
                  </Tooltip>
                </div>
              </div>
            )}
          </div>
        );
      case pathName.includes('/interviews/'):
        return (
          // 施設/対象者 sits on the right to match the 診断結果 bar above. Wraps instead of
          // colliding once the slack between the two groups runs out, so min-h- rather than h-.
          <div className="flex flex-wrap items-center justify-between gap-x-[30px] gap-y-1 bg-gainsboro px-6 py-2 text-lg text-black min-h-[56px] leading-10">
            <div className="flex flex-wrap items-center gap-x-10 gap-y-1">
              <p className="my-auto">面談詳細</p>
              {Object.keys(interviewDetail).length > 0 && (
                <div className="flex gap-2 font-bold whitespace-nowrap">
                  <span>実施日時：</span>
                  <span>{convertDate(String(interviewDetail.startTime?.replaceAll('Z', '')))}</span>
                  <span>
                    {convertTime(String(interviewDetail.startTime?.replaceAll('Z', '')))}-
                    {convertTime(String(interviewDetail.endTime?.replaceAll('Z', '')))}
                  </span>
                </div>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <div className="flex gap-2 whitespace-nowrap">
                <span className="my-auto">{apartmentIcon}</span>
                <span>{facilityNameSelected}</span>
              </div>
              <div className="flex gap-2 whitespace-nowrap">
                <IconCommonStyle name="account-circle" className="my-auto" />
                <span>{employeeNameSelected}</span>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      <div className={`!h-auto z-[1] relative`}>
        <div className="flex-col">
          <div className="w-full flex items-center justify-between px-10 bg-white mobile:px-4 md:pl-4 md:pr-6">
            <div className="flex items-center gap-2 min-w-0">
              <div className="flex items-center p-2 cursor-pointer hover:bg-slate-200 mobile:block md:hidden">
                <IconCommonStyle name="menu-bar" />
              </div>
              <img alt="logo" src={logo} className="h-[56px] shrink-0 object-contain mobile:h-10" />
            </div>
            <CommonDropdown items={items}>
              <div className="flex min-w-0 gap-2 items-center cursor-pointer">
                <p className="my-auto shrink-0">{interviewerIcon}</p>
                {!currentUser.consultant ? (
                  <p className="text-jet-black text-lg my-auto truncate mobile:text-base">
                    <span className="mobile:hidden">氏名：</span>
                    <span>{getNameUser}</span>
                  </p>
                ) : (
                  <p className="text-jet-black text-lg my-auto truncate mobile:text-base">
                    {currentUser?.name}
                  </p>
                )}
              </div>
            </CommonDropdown>
          </div>
          {currentUser.consultant && <>{getContentHeader(location.pathname)}</>}
        </div>
      </div>

      {(currentUser.com || currentUser.employee) && (
        <CommonModal
          open={isOpenModalAccountSetting}
          title={'アカウント設定'}
          onClose={handleClose}
        >
          <AccountSettingModal
            handleClose={handleClose}
            infoUser={Object.keys(infoCom).length !== 0 ? infoCom : infoEmployee}
          />
        </CommonModal>
      )}

      {currentUser.manager && (
        <CommonModal
          open={isOpenModalAccountSetting}
          title={'アカウント設定'}
          onClose={handleClose}
        >
          <AccountSettingModal
            handleClose={handleClose}
            infoUser={Object.keys(infoManager).length > 0 ? infoManager : currentUser.manager}
            readOnly
          />
        </CommonModal>
      )}
    </>
  );
}
